export type ValidationState = "designed" | "preview" | "executable" | "validated" | "production_ready";

export const platformPermissions = ["PERM-001-01", "PERM-001-02", "PERM-001-03"] as const;
export type PlatformPermission = typeof platformPermissions[number];
export type PlatformOwnerRole = "super_admin";

export type CustomerDeploymentState = "planned" | "launch_validation" | "active" | "paused";
export type IntegrationState = "not_configured" | "configured" | "connected" | "blocked";
export type ProductLabMode = "synthetic_state_machine" | "domain_service_replay" | "production_command_handlers";

export type CustomerFeature = Readonly<{
  id: string;
  label: string;
  domainId: string;
  capabilityKey: string;
  configured: boolean;
  state: ValidationState;
  evidence: string;
}>;

export type PlatformCustomer = Readonly<{
  key: string;
  name: string;
  mark: string;
  state: CustomerDeploymentState;
  instance: Readonly<{
    id: string;
    tenantId: string;
    workerName: string;
    environment: "production" | "staging";
    state: CustomerDeploymentState;
  }>;
  productionUrl: string;
  releaseKey: string;
  configurationPublicationKey: string;
  enabledCapabilities: readonly string[];
  integrations: readonly Readonly<{ key: string; label: string; state: IntegrationState; evidence: string }>[];
  health: Readonly<{
    state: "unknown" | "healthy" | "degraded" | "unavailable";
    basis: "not_probed" | "governed_probe";
    observedAt: string | null;
    evidence: string;
  }>;
  lab: Readonly<{
    state: "not_provisioned" | "provisioning" | "ready";
    mode: ProductLabMode;
    route: string;
    usesProductionCommandHandlers: boolean;
    exactConfigurationTwin: boolean;
    evidence: string;
  }>;
  features: readonly CustomerFeature[];
  projection: Readonly<{
    schemaVersion: 1;
    sha256: string;
    updatedAt: string;
  }>;
}>;

/** The strictly bounded payload stored in the shared control-plane projection. */
export type StoredPlatformCustomer = Readonly<Omit<PlatformCustomer, "projection">>;

export type PlatformBootstrap = Readonly<{
  owner: Readonly<{
    email: string;
    displayName: string;
    role: PlatformOwnerRole;
    permissions: readonly PlatformPermission[];
  }>;
  release: string;
  customers: readonly PlatformCustomer[];
}>;

export type PlatformHumanObservationQueueItem = Readonly<{
  observation_request_id: string;
  tenant_id: string;
  instance_id: string;
  state: "requested" | "observing" | "resolved" | "expired" | "revoked";
  importance: "low" | "normal" | "high" | "critical";
  business_blocking: boolean;
  risk: "low" | "standard" | "high" | "critical" | "undetermined";
  requested_at: string;
  updated_at: string;
  route_sha256: string;
  scope_sha256?: string;
  scope_expires_at?: string;
  support_identity_ready: boolean;
  lifecycle_delivery_ready: boolean;
  decision_state?: "approved" | "activation_authorized" | "revoked" | "resolved" | "expired";
}>;

export type PlatformHumanObservationQueueResponse = Readonly<{
  items: readonly PlatformHumanObservationQueueItem[];
}>;

export type PlatformHumanObservationDecision = Readonly<{
  decision_id: string;
  operation: "activate" | "revoke" | "resolve";
  state: "pending" | "applied" | "reconciliation_required" | "expired";
  activation_deadline?: string;
}>;

export type PlatformAuditEvent = Readonly<{
  id: string;
  actorEmail: string | null;
  action: string;
  targetType: string;
  targetKey: string | null;
  outcome: "allowed" | "denied" | "unavailable";
  correlationId: string;
  occurredAt: string;
}>;

export type PlatformAuditResponse = Readonly<{
  items: readonly PlatformAuditEvent[];
}>;

export type PlatformAdminReadiness = Readonly<{
  status: "ready" | "unavailable";
  dependencies: Readonly<{
    controlDb: "ready" | "unavailable";
    martinLabDb: "ready" | "unavailable";
  }>;
  capabilities: Readonly<{
    ownerNotification: Readonly<{ adapter: "resend"; state: "configured" | "not_configured" }>;
    humanObservationLifecycle: "ready" | "unavailable";
    ownerNotificationScheduler: "ready" | "unavailable";
  }>;
}>;
